import axios from "axios";

function transliterate(text) {
  const cyrillicToLatin = {
    а: "a",
    б: "b",
    в: "v",
    г: "g",
    д: "d",
    е: "e",
    ё: "e",
    ж: "zh",
    з: "z",
    и: "i",
    й: "i",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "h",
    ц: "ts",
    ч: "ch",
    ш: "sh",
    щ: "shch",
    ъ: "",
    ы: "y",
    ь: "",
    э: "e",
    ю: "iu",
    я: "ia",
  };

  if(!text || typeof text !== 'string') {
    return '';
  }

  return text
    .toLowerCase()
    .split("")
    .map((ch) => (cyrillicToLatin[ch] !== undefined ? cyrillicToLatin[ch] : ch))
    .join("");
}

export function kaspiLink(productName, productSKU) {
  let text = transliterate(productName);

  text = text.replace(/[^a-z0-9\s-]/g, "-");
  text = text.replace(/\s+/g, "-");
  text = text.replace(/-+/g, "-");
  text = text.replace(/^-+|-+$/g, "");

  return `https://kaspi.kz/shop/p/${text}-${productSKU}`;
}

export async function scrapeKaspiImage(url) {
  try {
    const response = await fetch(
      `${
        process.env.REACT_APP_SERVER_API
      }/kaspi-products/scrape-image/?url=${encodeURIComponent(url)}`
    );
    const data = await response.json();
    return data.image_url;
  } catch (error) {
    // Don't log error if request was aborted
    if (error.name !== "AbortError") {
      console.error("Error scraping image:", error);
    }
    return null;
  }
}

export const pollForTaskResult = async (
  taskId,
  maxAttempts = 3,
  interval = 3500
) => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_API}/kaspi-products/scrape-image/status/${taskId}/`
      );

      if (!response.ok) {
        console.error(`HTTP error for task ${taskId}: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "SUCCESS") {
        return result.image_url;
      } else if (result.status === "FAILURE") {
        throw new Error(result.error || "Task failed");
      } else if (result.status === "PENDING") {
        await new Promise((resolve) => setTimeout(resolve, interval));
        continue;
      } else {
        await new Promise((resolve) => setTimeout(resolve, interval));
        continue;
      }
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }

  throw new Error("Task timeout - maximum polling attempts reached");
};

export const fetchAllProductImages = async (products) => {
  console.log(`Starting image fetch for ${products.length} products`);

  // Step 1: Start all scraping tasks
  const taskPromises = products.map(async (product) => {
    try {
      const kaspiUrl = kaspiLink(product.product_name, product.sku);
      console.log(`Starting task for product ${product.sku}: ${kaspiUrl}`);

      const res = await fetch(
        `${
          process.env.REACT_APP_SERVER_API
        }/kaspi-products/scrape-image/?url=${encodeURIComponent(kaspiUrl)}`
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log(`Task started for product ${product.sku}:`, data);

      if (data.task_id) {
        return { product, taskId: data.task_id };
      } else {
        throw new Error(data.error || "Failed to start task");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(
          `Error starting image fetch task for product ${product.sku}:`,
          error
        );
      }
      return { product, taskId: null, error };
    }
  });

  const taskResults = await Promise.all(taskPromises);
  console.log(`Task results:`, taskResults);

  // Step 2: Poll for results of all tasks
  const resultPromises = taskResults.map(async ({ product, taskId, error }) => {
    if (error || !taskId) {
      console.log(
        `Skipping product ${product.sku} due to error or missing taskId`
      );
      return { ...product, imageUrl: null };
    }

    try {
      console.log(
        `Polling for result of product ${product.sku}, taskId: ${taskId}`
      );
      const imageUrl = await pollForTaskResult(taskId);
      console.log(`Got image URL for product ${product.sku}:`, imageUrl);
      return { ...product, imageUrl };
    } catch (error) {
      // Don't log error if request was aborted
      if (error.name !== "AbortError") {
        console.error(
          `Error fetching image for product ${product.sku}:`,
          error
        );
      }
      return { ...product, imageUrl: null };
    }
  });

  const results = await Promise.all(resultPromises);
  console.log(`Final results:`, results);
  return results;
};

export const asyncPaginateStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "32px",
    height: "auto",
    border: state.isFocused ? "1px solid #40a9ff" : "1px solid #d9d9d9",
    borderRadius: "6px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(24, 144, 255, 0.2)" : "none",
    fontSize: "14px",
    "&:hover": {
      borderColor: state.isFocused ? "#40a9ff" : "#40a9ff",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 11px",
    minHeight: "30px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0",
    padding: "0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: "flex-start",
    paddingTop: "1px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#bfbfbf",
    fontSize: "14px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#000000d9",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    margin: "2px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#000000d9",
    padding: "2px 6px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#00000073",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#ff4d4f",
      color: "white",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "6px",
    boxShadow:
      "0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#1890ff"
      : state.isFocused
      ? "#f5f5f5"
      : "white",
    color: state.isSelected ? "white" : "#000000d9",
    fontSize: "14px",
    padding: "5px 12px",
    "&:active": {
      backgroundColor: state.isSelected ? "#1890ff" : "#e6f7ff",
    },
  }),
  loadingMessage: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#00000073",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#00000073",
  }),
};

export const loadBrandOptions = async (
  searchValue,
  loadedOptions,
  { page },
  selectedName,
  selectedCategory,
  selectedSubcategory,
  selectedSubgroup,
  selectedType
) => {
  const token = localStorage?.getItem("token");
  if (!token) {
    console.error("No authentication token found");
    return {
      options: [],
      hasMore: false,
    };
  }

  try {
    const offset = (page - 1) * 50;
    const params = {
      name: searchValue || "",
      offset: offset,
    };

    // Add product_name filter if selected
    const productNameValues = extractCategoryValues(selectedName);
    if (productNameValues && productNameValues.length > 0) {
      params.product_name = productNameValues;
    }

    // Add root_category filter if selected
    const rootCategoryValues = extractCategoryValues(selectedCategory);
    if (rootCategoryValues && rootCategoryValues.length > 0) {
      params.root_category = rootCategoryValues;
    }

    // Add category_2 filter if selected
    const category2Values = extractCategoryValues(selectedSubcategory);
    if (category2Values && category2Values.length > 0) {
      params.category_2 = category2Values;
    }

    // Add category_3 filter if selected
    const category3Values = extractCategoryValues(selectedSubgroup);
    if (category3Values && category3Values.length > 0) {
      params.category_3 = category3Values;
    }

    // Add category_4 (Тип) filter if selected
    const category4Values = extractCategoryValues(selectedType);
    if (category4Values && category4Values.length > 0) {
      params.category_4 = category4Values;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/products-analytics/brands/`,
      {
        params: params,
        paramsSerializer: serializeParams,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const { brands, has_next } = response.data;

    const options = brands.map((brandName) => ({
      value: brandName,
      label: brandName,
    }));

    return {
      options,
      hasMore: has_next,
      additional: {
        page: page + 1,
      },
    };
  } catch (error) {
    console.error("Error fetching brands:", error);
    return {
      options: [],
      hasMore: false,
    };
  }
};

/**
 * Custom axios params serializer that properly handles arrays
 * Arrays are serialized as multiple query params with the same key
 * e.g., ['value1', 'value2'] becomes ?key=value1&key=value2
 */
const serializeParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
    } else if (value !== null && value !== undefined) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

/**
 * Extracts values from selected category items
 * Handles both single and multi-select formats
 * @param {Array|Object|string} selected - Selected category/categories
 * @returns {Array} Array of values
 */
const extractCategoryValues = (selected) => {
  if (!selected) return null;

  if (Array.isArray(selected)) {
    // Multi-select: array of objects [{value, label}, ...]
    return selected.map((item) => item.value);
  } else if (selected.value) {
    // Single select: object {value, label}
    return [selected.value];
  } else {
    // Fallback: direct string value
    return [selected];
  }
};

export const loadCategoryOptions = async (
  searchValue,
  loadedOptions,
  { page },
  selectedBrand,
  selectedName,
  selectedSubcategory,
  selectedSubgroup,
  selectedType
) => {
  const token = localStorage?.getItem("token");
  if (!token) {
    console.error("No authentication token found");
    return { options: [], hasMore: false };
  }

  try {
    const offset = (page - 1) * 50;
    const params = {
      name: searchValue || "",
      offset: offset,
      level: 1,
    };

    // Add brand filter if selected
    const brandValues = extractCategoryValues(selectedBrand);
    if (brandValues && brandValues.length > 0) {
      params.brand = brandValues;
    }

    // Add nomination filter if selected
    const nominationValues = extractCategoryValues(selectedName);
    if (nominationValues && nominationValues.length > 0) {
      params.product_name = nominationValues;
    }

    // Narrowing by deeper levels (cat_2/3/4) keeps options consistent
    const category2Values = extractCategoryValues(selectedSubcategory);
    if (category2Values && category2Values.length > 0) {
      params.category_2 = category2Values;
    }
    const category3Values = extractCategoryValues(selectedSubgroup);
    if (category3Values && category3Values.length > 0) {
      params.category_3 = category3Values;
    }
    const category4Values = extractCategoryValues(selectedType);
    if (category4Values && category4Values.length > 0) {
      params.category_4 = category4Values;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/products-analytics/categories-data/`,
      {
        params: params,
        paramsSerializer: serializeParams,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const { categories, has_next } = response.data;

    const options = categories.map((categoryName) => ({
      value: categoryName,
      label: categoryName,
    }));

    return {
      options,
      hasMore: has_next,
      additional: { page: page + 1 },
    };
  } catch (error) {
    console.error("Error fetching categories:", error);
    return { options: [], hasMore: false };
  }
};

export const checkAnalyticProfileTariff = async () => {
  const token = localStorage?.getItem("token");
  if (!token) {
    return false;
  }

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/tariffs/has-analytics-profile/`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    return response.status === 200;
  } catch (error) {
    console.error("Error checking tariff:", error);
    return false;
  }
};

export const loadSubcategoryOptions = async (
  searchValue,
  loadedOptions,
  { page },
  selectedBrand,
  selectedName,
  selectedCategory,
  selectedSubgroup,
  selectedType
) => {
  const token = localStorage?.getItem("token");
  if (!token) {
    return { options: [], hasMore: false };
  }

  try {
    const offset = (page - 1) * 50;
    const params = {
      name: searchValue || "",
      offset: offset,
      level: 2,
    };

    // Add brand filter if selected
    const brandValues = extractCategoryValues(selectedBrand);
    if (brandValues && brandValues.length > 0) {
      params.brand = brandValues;
    }

    // Add product_name filter if selected
    const productNameValues = extractCategoryValues(selectedName);
    if (productNameValues && productNameValues.length > 0) {
      params.product_name = productNameValues;
    }

    // Add root_category filter if selected
    const rootCategoryValues = extractCategoryValues(selectedCategory);
    if (rootCategoryValues && rootCategoryValues.length > 0) {
      params.root_category = rootCategoryValues;
    }

    // Deeper-level narrowing (cat_3/4)
    const category3Values = extractCategoryValues(selectedSubgroup);
    if (category3Values && category3Values.length > 0) {
      params.category_3 = category3Values;
    }
    const category4Values = extractCategoryValues(selectedType);
    if (category4Values && category4Values.length > 0) {
      params.category_4 = category4Values;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/products-analytics/categories-data/`,
      {
        params: params,
        paramsSerializer: serializeParams,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const { categories, has_next } = response.data;
    const options = categories.map((subcategoryName) => ({
      value: subcategoryName,
      label: subcategoryName,
    }));

    return {
      options,
      hasMore: has_next,
      additional: { page: page + 1 },
    };
  } catch (error) {
    console.error("Error fetching subcategories:", error);
    return { options: [], hasMore: false };
  }
};

/**
 * Load options function for Subgroups (depends on category and subcategory)
 * Properly handles multi-select arrays and serializes them for the backend
 */
export const loadSubgroupOptions = async (
  searchValue,
  loadedOptions,
  { page },
  selectedBrand,
  selectedName,
  selectedCategory,
  selectedSubcategory,
  selectedType
) => {
  const token = localStorage?.getItem("token");
  if (!token) {
    return { options: [], hasMore: false };
  }

  try {
    const offset = (page - 1) * 50;
    const params = {
      name: searchValue || "",
      offset: offset,
      level: 3,
    };

    // Add brand filter if selected
    const brandValues = extractCategoryValues(selectedBrand);
    if (brandValues && brandValues.length > 0) {
      params.brand = brandValues;
    }

    // Add product_name filter if selected
    const productNameValues = extractCategoryValues(selectedName);
    if (productNameValues && productNameValues.length > 0) {
      params.product_name = productNameValues;
    }

    // Add root_category filter if selected
    const rootCategoryValues = extractCategoryValues(selectedCategory);
    if (rootCategoryValues && rootCategoryValues.length > 0) {
      params.root_category = rootCategoryValues;
    }

    // Add category_2 filter if selected
    const category2Values = extractCategoryValues(selectedSubcategory);
    if (category2Values && category2Values.length > 0) {
      params.category_2 = category2Values;
    }

    // Deeper-level narrowing (cat_4)
    const category4Values = extractCategoryValues(selectedType);
    if (category4Values && category4Values.length > 0) {
      params.category_4 = category4Values;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/products-analytics/categories-data/`,
      {
        params: params,
        paramsSerializer: serializeParams,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const { categories, has_next } = response.data;
    const options = categories.map((subgroupName) => ({
      value: subgroupName,
      label: subgroupName,
    }));

    return {
      options,
      hasMore: has_next,
      additional: { page: page + 1 },
    };
  } catch (error) {
    console.error("Error fetching subgroups:", error);
    return { options: [], hasMore: false };
  }
};

// Load options function for Тип (level 4)
export const loadTypeOptions = async (
  searchValue,
  loadedOptions,
  { page },
  selectedBrand,
  selectedName,
  selectedCategory,
  selectedSubcategory,
  selectedSubgroup
) => {
  const token = localStorage?.getItem("token");
  if (!token) {
    return { options: [], hasMore: false };
  }

  try {
    const offset = (page - 1) * 50;
    const params = {
      name: searchValue || "",
      offset: offset,
      level: 4,
    };

    const brandValues = extractCategoryValues(selectedBrand);
    if (brandValues && brandValues.length > 0) {
      params.brand = brandValues;
    }
    const productNameValues = extractCategoryValues(selectedName);
    if (productNameValues && productNameValues.length > 0) {
      params.product_name = productNameValues;
    }
    const rootCategoryValues = extractCategoryValues(selectedCategory);
    if (rootCategoryValues && rootCategoryValues.length > 0) {
      params.root_category = rootCategoryValues;
    }
    const category2Values = extractCategoryValues(selectedSubcategory);
    if (category2Values && category2Values.length > 0) {
      params.category_2 = category2Values;
    }
    const category3Values = extractCategoryValues(selectedSubgroup);
    if (category3Values && category3Values.length > 0) {
      params.category_3 = category3Values;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/products-analytics/categories-data/`,
      {
        params: params,
        paramsSerializer: serializeParams,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const { categories, has_next } = response.data;
    const options = categories.map((typeName) => ({
      value: typeName,
      label: typeName,
    }));

    return {
      options,
      hasMore: has_next,
      additional: { page: page + 1 },
    };
  } catch (error) {
    console.error("Error fetching types:", error);
    return { options: [], hasMore: false };
  }
};

// Load options function for Nominations
export const loadNominationOptions = async (
  searchValue,
  loadedOptions,
  { page },
  selectedBrand,
  selectedCategory,
  selectedSubcategory,
  selectedSubgroup,
  selectedType
) => {
  const token = localStorage?.getItem("token");
  if (!token) {
    console.error("No authentication token found");
    return { options: [], hasMore: false };
  }

  try {
    const offset = (page - 1) * 50;
    const params = {
      name: searchValue || "",
      offset: offset,
    };

    // Add brand filter if selected
    const brandValues = extractCategoryValues(selectedBrand);
    if (brandValues && brandValues.length > 0) {
      params.brand = brandValues;
    }

    // Add root_category filter if selected
    const rootCategoryValues = extractCategoryValues(selectedCategory);
    if (rootCategoryValues && rootCategoryValues.length > 0) {
      params.root_category = rootCategoryValues;
    }

    // Add category_2 filter if selected
    const category2Values = extractCategoryValues(selectedSubcategory);
    if (category2Values && category2Values.length > 0) {
      params.category_2 = category2Values;
    }

    // Add category_3 filter if selected
    const category3Values = extractCategoryValues(selectedSubgroup);
    if (category3Values && category3Values.length > 0) {
      params.category_3 = category3Values;
    }

    // Add category_4 (Тип) filter if selected
    const category4Values = extractCategoryValues(selectedType);
    if (category4Values && category4Values.length > 0) {
      params.category_4 = category4Values;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_API}/products-analytics/nominations/`,
      {
        params: params,
        paramsSerializer: serializeParams,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      }
    );

    const { product_names, has_next } = response.data;
    const options = product_names.map((nominationName) => ({
      value: nominationName,
      label: nominationName,
    }));

    return {
      options,
      hasMore: has_next,
      additional: { page: page + 1 },
    };
  } catch (error) {
    console.error("Error fetching nominations:", error);
    return { options: [], hasMore: false };
  }
};
